import type { JSONContent } from '@tiptap/react';
import type { Block } from '../types';
import { newBlockId } from '../blockId';
import { htmlToInline, inlineToHtml } from './richtextHtml';
import { NODE_TO_ADVANCED_BLOCK } from './advancedBlockMap';

/**
 * TipTap document JSON → Block[] (ported from Scilora `serialization/tiptap-to-blocks.ts`).
 */
export function tiptapToBlocks(doc: JSONContent): Block[] {
    if (!doc.content) return [];
    const blocks = doc.content.map(nodeToBlock).filter((b): b is Block => b !== null);
    // #526: the editor keeps an empty trailing text block (see blocksToTiptap /
    // TrailingNode); it is editor chrome, not content.
    const last = blocks[blocks.length - 1];
    if (blocks.length > 1 && last.type === 'richtext' && (last.data as { html?: string }).html === '' && !last.meta) {
        blocks.pop();
    }
    return blocks;
}

function nodeToBlock(node: JSONContent): Block | null {
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const blockId = (attrs.blockId as string) || newBlockId();
    const blockMeta = (attrs.blockMeta as Record<string, unknown>) ?? undefined;

    switch (node.type) {
        case 'richtextBlock': {
            const originalHtml = (attrs.html as string) ?? '';
            const html = inlineToHtml(node.content);
            // Keep the stored markup while the visible text is unchanged (lists,
            // image-only html …). #526: an EMPTY node must not fall back to the
            // stored html — Enter copies the attrs onto the split-off block, which
            // turned every new empty line into a duplicate of the sentence above.
            const unchanged = originalHtml !== '' && inlineToHtml(htmlToInline(originalHtml)) === html;
            return { id: blockId, type: 'richtext', data: { html: unchanged ? originalHtml : html }, meta: blockMeta };
        }
        case 'headingBlock':
            return { id: blockId, type: 'heading', data: { text: extractText(node), level: (attrs.level as number) ?? 2 }, meta: blockMeta };
        case 'imageBlock':
            return {
                id: blockId,
                type: 'image',
                data: pickDefined({
                    src: attrs.src as string,
                    alt: attrs.alt as string,
                    caption: attrs.caption as string,
                    alignment: attrs.alignment as string,
                    mediaFileUuid: attrs.mediaFileUuid as string,
                }),
                meta: blockMeta,
            };
        case 'dividerBlock':
            return { id: blockId, type: 'divider', data: {}, meta: blockMeta };
        case 'calloutBlock':
            return {
                id: blockId,
                type: 'callout',
                data: pickDefined({ html: attrs.html as string, variant: attrs.variant as string, icon: attrs.icon as string }),
                meta: blockMeta,
            };
        case 'quoteBlock':
            return { id: blockId, type: 'quote', data: pickDefined({ text: extractText(node), attribution: attrs.attribution as string }), meta: blockMeta };
        case 'codeBlockNode':
            return {
                id: blockId,
                type: 'code',
                data: pickDefined({
                    code: extractText(node),
                    language: attrs.language as string,
                    showLineNumbers: attrs.showLineNumbers as boolean,
                    caption: attrs.caption as string,
                }),
                meta: blockMeta,
            };
        case 'videoBlock':
            return { id: blockId, type: 'video', data: pickKeys(attrs, ['src', 'poster', 'caption', 'autoplay', 'muted', 'controls', 'mediaFileUuid']), meta: blockMeta };
        case 'audioBlock':
            return { id: blockId, type: 'audio', data: pickKeys(attrs, ['src', 'title', 'autoplay', 'mediaFileUuid']), meta: blockMeta };
        case 'fileBlock':
            return { id: blockId, type: 'file', data: pickKeys(attrs, ['src', 'filename', 'filesize', 'mimeType', 'mediaFileUuid']), meta: blockMeta };
        case 'embedBlock':
            return { id: blockId, type: 'embed', data: pickKeys(attrs, ['url', 'provider', 'caption', 'aspectRatio']), meta: blockMeta };
        case 'columnsBlock':
            return { id: blockId, type: 'columns', data: pickDefined({ columns: attrs.columns as unknown[] }), meta: blockMeta };
        case 'accordionBlock':
            return { id: blockId, type: 'accordion', data: pickDefined({ items: attrs.items as unknown[], allowMultiple: attrs.allowMultiple as boolean }), meta: blockMeta };
        case 'tabsBlock':
            return { id: blockId, type: 'tabs', data: pickDefined({ tabs: attrs.tabs as unknown[] }), meta: blockMeta };
        case 'tableBlock':
            return {
                id: blockId,
                type: 'table',
                data: pickDefined({ rows: attrs.rows as unknown[], hasHeader: attrs.hasHeader as boolean, caption: attrs.caption as string }),
                meta: blockMeta,
            };
        case 'inlineQuizBlock':
            return {
                id: blockId,
                type: 'inline_quiz',
                data: pickDefined({
                    question: attrs.question as string,
                    choices: attrs.choices as unknown[],
                    multiSelect: attrs.multiSelect as boolean,
                    explanation: attrs.explanation as string,
                }),
                meta: blockMeta,
            };
        case 'fillBlankBlock':
            return {
                id: blockId,
                type: 'fill_blank',
                data: pickDefined({ template: attrs.template as string, blanks: attrs.blanks as unknown[], explanation: attrs.explanation as string }),
                meta: blockMeta,
            };
        case 'matchingBlock':
            return { id: blockId, type: 'matching', data: pickDefined({ pairs: attrs.pairs as unknown[], explanation: attrs.explanation as string }), meta: blockMeta };
        case 'orderingBlock':
            return { id: blockId, type: 'ordering', data: pickDefined({ items: attrs.items as unknown[], explanation: attrs.explanation as string }), meta: blockMeta };
        case 'flashcardBlock':
            return { id: blockId, type: 'flashcard', data: pickDefined({ cards: attrs.cards as unknown[] }), meta: blockMeta };
        case 'discussionBlock':
            return {
                id: blockId,
                type: 'discussion',
                data: pickDefined({
                    prompt: attrs.prompt as string,
                    allowReplies: attrs.allowReplies as boolean,
                    moderationRequired: attrs.moderationRequired as boolean,
                }),
                meta: blockMeta,
            };
        case 'pollBlock':
            return {
                id: blockId,
                type: 'poll',
                data: pickDefined({
                    question: attrs.question as string,
                    options: attrs.options as unknown[],
                    multiSelect: attrs.multiSelect as boolean,
                    showResults: attrs.showResults as boolean,
                }),
                meta: blockMeta,
            };
        case 'scormEmbedBlock':
            return {
                id: blockId,
                type: 'scorm_embed',
                data: pickDefined({
                    packageUrl: attrs.packageUrl as string,
                    title: attrs.title as string,
                    version: attrs.version as string,
                    scormPackageId: attrs.scormPackageId as number,
                }),
                meta: blockMeta,
            };
        case 'paragraph':
            return { id: blockId, type: 'richtext', data: { html: '' }, meta: blockMeta };
        default: {
            const advancedType = NODE_TO_ADVANCED_BLOCK[node.type ?? ''];
            if (advancedType) {
                const dataAttrs = { ...attrs };
                delete dataAttrs.blockId;
                delete dataAttrs.blockMeta;
                return { id: blockId, type: advancedType, data: pickDefined(dataAttrs), meta: blockMeta };
            }
            if (attrs.originalType) {
                return { id: blockId, type: attrs.originalType as string, data: (attrs.data as Record<string, unknown>) ?? {}, meta: blockMeta };
            }
            return null;
        }
    }
}

function extractText(node: JSONContent): string {
    if (!node.content) return '';
    return node.content.filter((child) => child.type === 'text').map((child) => child.text ?? '').join('');
}

function pickDefined(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined && value !== null && value !== '') {
            result[key] = value;
        }
    }
    return result;
}

function pickKeys(attrs: Record<string, unknown>, keys: string[]): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const key of keys) {
        if (attrs[key] !== undefined && attrs[key] !== null && attrs[key] !== '') {
            result[key] = attrs[key];
        }
    }
    return result;
}
